import { fetchWithAuth } from "./fetch-with-auth";
import { API_URL } from "constants/index";

export const DOWNLOAD_ZIP_MAX_FILES = 200;

export interface DownloadFilesZipResult {
  includedCount: number;
  skippedCount: number;
}

/**
 * Bundles the given file IDs into a ZIP on the backend and triggers a browser
 * download. Returns include/skip counts so callers can surface a toast when
 * some files couldn't be included.
 */
export async function downloadFilesZip(
  fileIds: number[],
  session: any,
): Promise<DownloadFilesZipResult> {
  if (!session) {
    throw new Error("Geen actieve sessie — log opnieuw in");
  }
  if (!Array.isArray(fileIds) || fileIds.length === 0) {
    throw new Error("Geen bestanden geselecteerd");
  }
  if (fileIds.length > DOWNLOAD_ZIP_MAX_FILES) {
    throw new Error(
      `Je kan maximaal ${DOWNLOAD_ZIP_MAX_FILES} bestanden tegelijk downloaden`,
    );
  }

  const response = await fetchWithAuth(
    `${API_URL}/file/download-zip`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fileIds }),
    },
    session,
  );

  if (!response.ok) {
    let message = `Download mislukt (${response.status})`;
    try {
      const data = await response.json();
      if (data?.message) {
        message = Array.isArray(data.message) ? data.message.join(", ") : data.message;
      }
    } catch {
      // ignore parse error, keep default message
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const includedCount = Number(response.headers.get("X-Included-Count") || "0");
  const skippedCount = Number(response.headers.get("X-Skipped-Count") || "0");

  const disposition = response.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename =
    match?.[1] ||
    `yallyhub-bestanden-${new Date().toISOString().replace(/[:.]/g, "-")}.zip`;

  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    // Delay revoke so the browser has time to start the download.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  return { includedCount, skippedCount };
}
